import React from 'react'
import { Formik, Form, FormikProps } from 'formik'
import { useNewTranslation } from 'hooks/index'
import i18n from 'config/i18n'
import * as yup from 'yup'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { SubmissionState, PartiposterProfile } from 'state/submissions'
import { EngagementId } from 'state/tasks'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { ActionCell } from 'components/Shared/MuiTable/Cell/ActionCell'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { MediaCell, MediaCellProps } from 'components/Shared/MuiTable/Cell/MediaCell'
import Inputs, { NumberInputGroup } from 'components/Inputs'
import { getInfoTooltipHeaderCell } from 'components/Shared/MuiTable/Cell/InfoTooltipHeaderCell'
import { PLATFORM_ENUM, STEP_TYPE_ENUM } from 'services/api/constants'
import { DataValidationProps } from './EngagementScreenshotTableDef'
import { buildEngagementFieldColDef, sortEngagementType } from '../customTaskEngagementFieldsConfig'
import { getEngagementHeaderTranslation } from 'components/utils/translationsMap'
import RectangleButton from 'particles/Button/RectangleButton'
import Text from 'particles/Text'
import { SubmissionFormContainer } from './styles'
import { GridColDefProps } from '../SimpleTable/constants'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { convertSecondsToTime, convertTimeStringToSeconds } from 'components/utils'

export interface RowsProps {
    // Note: Row id is treated from MUI DataGridPro component as list key, don't use index for dynamic list key
    // issue related: https://gitlab.com/partipost-dev/engineering/backlog-items/-/issues/246
    id: string
    uuid: string
    name?: {
        value: string
        color?: string
    }
    igUsername?: {
        account: string | null
        socialDomain: string
    }
    tiktokUsername?: {
        account: string | null
        socialDomain: string
    }
    fbUsername?: {
        account: string | null
        socialDomain: string
    }
    twitterUsername?: {
        account: string | null
        socialDomain: string
    }
    xiaohongshuUsername?: {
        account: string | null
        socialDomain: string
    }
    view_count_screenshot_files?: string[]
    insights_screenshot_files: string[]
    follower_count: {
        value: string
        color?: string
    }
    engagement_impression_count?: {
        value: string
        color?: string
    }
    engagement_like_count?: {
        value: string
        color?: string
    }
    engagement_reach_count?: {
        value: string
        color?: string
    }
    engagement_view_count?: {
        value: string
        color?: string
    }
    engagement_comment_count?: {
        value: string
        color?: string
    }
    engagement_tag_count?: {
        value: string
        color?: string
    }
    engagement_share_count?: {
        value: string
        color?: string
    }
    engagement_cta_click_count?: {
        value: string
        color?: string
    }
    engagement_save_count?: {
        value: string
        color?: string
    }
    // IG Story Only
    engagement_sticker_tap_count?: {
        value: string
        color?: string
    }
    engagement_link_click_count?: {
        value: string
        color?: string
    }
    // Twitter only
    engagement_reply_count?: {
        value: string
        color?: string
    }
    engagement_retweet_count?: {
        value: string
        color?: string
    }
    engagement_engagement_count?: {
        value: string
        color?: string
    }
    engagement_total_watch_time_second_count?: {
        value: string
        color?: string
    }
    engagement_total_play_time_second_count?: {
        value: string
        color?: string
    }
    engagement_watched_full_video_percentage_count?: {
        value: string
        color?: string
    }
    engagement_average_watch_time_second_count?: {
        value: string
        color?: string
    }
    partiposterTags: {
        profileData: PartiposterProfile
        submissionUuid: string
        campaignUuid: string
    }
    isMultiSelectMode: boolean
}

interface GetColumnsParams {
    engagementTypes: EngagementId[]
    isMultiSelectMode: boolean
    stepType: STEP_TYPE_ENUM
    searchablePlatforms: PLATFORM_ENUM[]
    canManageTag: boolean
    disableRequestEdit?: boolean
    disableApprove?: boolean
    isCampaignEnded?: boolean
    updateViewedRow?: (id: string) => void
}

export const getColumns = ({
    engagementTypes,
    isMultiSelectMode,
    stepType,
    searchablePlatforms,
    canManageTag,
    disableRequestEdit,
    disableApprove,
    isCampaignEnded,
    updateViewedRow,
}: GetColumnsParams): GridColDefProps[] => {
    const hasPlatform = (platform: PLATFORM_ENUM) => {
        return searchablePlatforms.includes(platform)
    }

    return [
        {
            field: 'profile',
            type: 'string',
            headerName: i18n.t('submission.table.profile'),
            width: 160,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
            subFields: [
                {
                    field: 'name',
                    headerName: i18n.t('submission.table.nameLabel'),
                },
                {
                    field: 'gender',
                    headerName: i18n.t('submission.table.genderLabel'),
                },
                {
                    field: 'age',
                    headerName: i18n.t('submission.table.ageLabel'),
                },
            ],
        },
        {
            field: 'socialMediaInfo',
            type: 'string',
            headerName: i18n.t('submission.table.socialMediaInfo'),
            width: 180,
            minWidth: 60,
            sortable: false,
            renderCell: function SocialAccount(props: GridRenderCellParams): React.ReactElement {
                const handleUpdateViewed = () => {
                    if (updateViewedRow) {
                        updateViewedRow(props.row.uuid)
                    }
                }
                return <SocialMediaInfoCell {...props} onClick={handleUpdateViewed} />
            },
            subFields: [
                ...(hasPlatform(PLATFORM_ENUM.IG)
                    ? [
                          {
                              field: FIELDS_TYPE.IG_FOLLOWERS,
                              headerName: i18n.t('submission.table.instagramFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.IG_USERNAME,
                              headerName: i18n.t('submission.table.instagramUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.FB)
                    ? [
                          {
                              field: FIELDS_TYPE.FB_FRIENDS,
                              headerName: i18n.t('submission.table.facebookFriendsLabel'),
                          },
                          {
                              field: FIELDS_TYPE.FB_USERNAME,
                              headerName: i18n.t('submission.table.facebookUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                    ? [
                          {
                              field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                              headerName: i18n.t('submission.table.tikTokFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TIKTOK_USERNAME,
                              headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                    ? [
                          {
                              field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                              headerName: i18n.t('submission.table.twitterFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TWITTER_USERNAME,
                              headerName: i18n.t('submission.table.twitterUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.XIAOHONGSHU)
                    ? [
                          {
                              field: FIELDS_TYPE.XIAOHONGSHU_USERNAME,
                              headerName: i18n.t('submission.table.XHSUsernameLabel'),
                          },
                          {
                              field: FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS,
                              headerName: i18n.t('submission.table.XHSFollowersLabel'),
                          },
                      ]
                    : []),
            ],
        },
        ...(stepType === STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_SCREENSHOT
            ? [
                  {
                      field: 'view_count_screenshot_files',
                      headerName: i18n.t('submission.table.storyScreenshotLabel'),
                      width: 175,
                      renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        {
            field: 'insights_screenshot_files',
            headerName: i18n.t('submission.table.engagementScreenshotLabel'),
            width: 220,
            renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
            sortable: false,
            filterable: false,
        },
        {
            field: 'follower_count',
            headerName: i18n.t('submission.table.followerCountLabel'),
            width: 240,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: getInfoTooltipHeaderCell({
                width: '190px',
                title: i18n.t('submission.table.followerCountLabel'),
                description: i18n.t(
                    'submission.table.followerCountTooltip',
                    'The number Partiposters entered when submitting engagement result for the campaign',
                ),
            }),
        },
        ...(engagementTypes.includes(EngagementId.TOTAL_PLAY_TIME)
            ? [
                  {
                      field: 'engagement_total_play_time_second_count',
                      headerName: i18n.t('submission.campaignReport.metric.totalPlayTime'),
                      width: 105,
                      sortable: false,
                      filterable: false,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                  },
              ]
            : []),
        ...(engagementTypes.includes(EngagementId.TOTAL_WATCH_TIME)
            ? [
                  {
                      field: 'engagement_total_watch_time_second_count',
                      headerName: i18n.t('engagementMetric.totalWatchTime'),
                      width: 105,
                      sortable: false,
                      filterable: false,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                  },
              ]
            : []),
        ...(engagementTypes.includes(EngagementId.WATCHED_FULL_VIDEO)
            ? [
                  {
                      field: 'engagement_watched_full_video_percentage_count',
                      headerName: i18n.t('engagementMetric.watchedFullVideo'),
                      width: 105,
                      sortable: false,
                      filterable: false,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                  },
              ]
            : []),
        ...(engagementTypes.includes(EngagementId.AVERAGE_WATCH_TIME)
            ? [
                  {
                      field: 'engagement_average_watch_time_second_count',
                      headerName: i18n.t('engagementMetric.averageWatchTime'),
                      width: 105,
                      sortable: false,
                      filterable: false,
                      renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                  },
              ]
            : []),

        ...(sortEngagementType(stepType, engagementTypes)
            .map(buildEngagementFieldColDef)
            .filter(item => item !== null) as GridColDef[]),
        ...(canManageTag
            ? [
                  {
                      field: 'partiposterTags',
                      headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                      width: 240,
                      minWidth: 200,
                      renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        ...(!isMultiSelectMode && !isCampaignEnded
            ? [
                  {
                      field: 'actions',
                      headerName: i18n.t('submission.table.actionsLabel'),
                      renderCell: (props: GridRenderCellParams) => (
                          <ActionCell
                              {...props}
                              disableRequestEdit={disableRequestEdit}
                              disableApprove={disableApprove}
                          />
                      ),
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
    ]
}

const isTimeDurationMetric = (engagementType: EngagementId) => {
    return (
        engagementType === EngagementId.TOTAL_WATCH_TIME ||
        engagementType === EngagementId.TOTAL_PLAY_TIME ||
        engagementType === EngagementId.AVERAGE_WATCH_TIME
    )
}

export const getRows = (
    campaignUuid: string,
    submissions: SubmissionState[],
    profileById: Record<string, PartiposterProfile>,
    isMultiSelectMode: boolean,
): RowsProps[] => {
    return submissions.map((submission: SubmissionState) => {
        const profile = profileById[submission.partiposterId]
        const { social_media_accounts: socialMediaAccounts } = profile

        const igProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.IG)
        const tiktokProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TIKTOK)
        const fbProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.FB)
        const twitterProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TWITTER)
        const xiaohongshuProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.XIAOHONGSHU)
        return {
            id: submission.uuid,
            uuid: submission.uuid,
            profile: {
                profile,
            },
            socialMediaInfo: {
                profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile, xiaohongshuProfile],
            },
            insights_screenshot_files: submission.data && submission.data.insights_screenshot_files,
            view_count_screenshot_files: submission.data && submission.data.view_count_screenshot_files,
            follower_count: { value: submission.data && submission.data.follower_count },
            engagement_impression_count: { value: submission.data && submission.data.engagement_impression_count },
            engagement_like_count: { value: submission.data && submission.data.engagement_like_count },
            engagement_reach_count: { value: submission.data && submission.data.engagement_reach_count },
            engagement_view_count: { value: submission.data && submission.data.engagement_view_count },
            engagement_comment_count: { value: submission.data && submission.data.engagement_comment_count },
            engagement_tag_count: { value: submission.data && submission.data.engagement_tag_count },
            engagement_share_count: { value: submission.data && submission.data.engagement_share_count },
            // Tiktok Post Only
            engagement_cta_click_count: { value: submission.data && submission.data.engagement_cta_click_count },
            // Tiktok Post and Ig Post Only
            engagement_save_count: { value: submission.data && submission.data.engagement_save_count },
            // IG Story Only
            engagement_sticker_tap_count: { value: submission.data && submission.data.engagement_sticker_tap_count },
            engagement_link_click_count: { value: submission.data && submission.data.engagement_link_click_count },
            // Twitter Only
            engagement_reply_count: { value: submission.data && submission.data.engagement_reply_count },
            engagement_retweet_count: { value: submission.data && submission.data.engagement_retweet_count },
            engagement_engagement_count: { value: submission.data && submission.data.engagement_engagement_count },
            engagement_total_watch_time_second_count: {
                value:
                    submission.data && convertSecondsToTime(submission.data.engagement_total_watch_time_second_count),
            },
            engagement_total_play_time_second_count: {
                value: submission.data && convertSecondsToTime(submission.data.engagement_total_play_time_second_count),
            },
            engagement_watched_full_video_percentage_count: {
                value: submission.data && `${submission.data.engagement_watched_full_video_percentage_count}%`,
            },
            engagement_average_watch_time_second_count: {
                value:
                    submission.data && convertSecondsToTime(submission.data.engagement_average_watch_time_second_count),
            },
            partiposterTags: {
                profileData: profile,
                submissionUuid: submission.submission_uuid,
                campaignUuid: campaignUuid,
            },
            isMultiSelectMode: isMultiSelectMode,
        }
    })
}

export const ApproveSubmission: React.FC<{
    handleApprove: any
    data: any
    handleClear: any
    engagementTypes: EngagementId[]
    stepType: STEP_TYPE_ENUM
}> = ({ handleApprove, data, handleClear, engagementTypes, stepType }) => {
    const [newT] = useNewTranslation()
    const dataValidatation: DataValidationProps = {}
    engagementTypes.map((engagementType: EngagementId) => {
        const key = 'engagement_' + engagementType + '_count'
        const engagementHeader = getEngagementHeaderTranslation[engagementType as EngagementId]()
        if (isTimeDurationMetric(engagementType)) {
            dataValidatation[key] = yup
                .string()
                .required(newT('errorMessage.validation.enterMissingMetric', { engagementMetric: engagementHeader }))
                .test('is-number', newT('Please enter correct time format e.g. 3h:15m:10s'), value => {
                    return !!convertTimeStringToSeconds(value)
                })
        } else {
            dataValidatation[key] = yup
                .number()
                .min(0, newT('errorMessage.validation.enterNonNegativeNumbers'))
                .required(newT('errorMessage.validation.enterMissingMetric', { engagementMetric: engagementHeader }))
        }
    })
    const followerCountHeader = getEngagementHeaderTranslation['follower_count']()
    dataValidatation['follower_count'] = yup
        .number()
        .min(0, newT('errorMessage.validation.enterNonNegativeNumbers'))
        .required(newT('errorMessage.validation.enterMissingMetric', { engagementMetric: followerCountHeader }))

    const validation = yup.object({
        comment: yup
            .string()
            .nullable()
            .max(10000, ({ max }) =>
                newT(
                    'errorMessage.validation.enterLessThanCharacterLimit',
                    'Please enter less than {{max}} characters.',
                    {
                        max,
                    },
                ),
            ),
        data: yup.object(dataValidatation),
    })

    return (
        <Formik
            initialValues={{
                comment: data.comment,
                data: {
                    follower_count: data.data.follower_count,
                    engagement_impression_count: data.data.engagement_impression_count,
                    engagement_like_count: data.data.engagement_like_count,
                    engagement_reach_count: data.data.engagement_reach_count,
                    engagement_view_count: data.data.engagement_view_count,
                    engagement_comment_count: data.data.engagement_comment_count,
                    engagement_tag_count: data.data.engagement_tag_count,
                    engagement_share_count: data.data.engagement_share_count,
                    // IG Story only
                    engagement_sticker_tap_count: data.data.engagement_sticker_tap_count,
                    engagement_link_click_count: data.data.engagement_link_click_count,
                    // Tiktok Only
                    engagement_cta_click_count: data.data.engagement_cta_click_count,
                    // Tiktok and Ig post Only
                    engagement_save_count: data.data.engagement_save_count,
                    // Twitter Only
                    engagement_reply_count: data.data.engagement_reply_count,
                    engagement_retweet_count: data.data.engagement_retweet_count,
                    engagement_engagement_count: data.data.engagement_engagement_count,
                    engagement_total_watch_time_second_count: convertSecondsToTime(
                        data.data.engagement_total_watch_time_second_count,
                    ),
                    engagement_total_play_time_second_count: convertSecondsToTime(
                        data.data.engagement_total_play_time_second_count,
                    ),
                    engagement_watched_full_video_percentage_count:
                        data.data.engagement_watched_full_video_percentage_count,
                    engagement_average_watch_time_second_count: convertSecondsToTime(
                        data.data.engagement_average_watch_time_second_count,
                    ),
                },
            }}
            validationSchema={validation}
            enableReinitialize={true}
            onSubmit={(values: { comment: string | null; data: any }, actions): void => {
                const comment = values.comment || newT('app.defaultApproveComment')
                values.data.engagement_total_watch_time_second_count = convertTimeStringToSeconds(
                    values.data.engagement_total_watch_time_second_count,
                )
                values.data.engagement_total_play_time_second_count = convertTimeStringToSeconds(
                    values.data.engagement_total_play_time_second_count,
                )
                values.data.engagement_average_watch_time_second_count = convertTimeStringToSeconds(
                    values.data.engagement_average_watch_time_second_count,
                )
                handleApprove(data.uuid, comment, values.data)
                handleClear()
                actions.setSubmitting(false)
            }}
        >
            {(formikBag: FormikProps<any>) => {
                return (
                    <Form>
                        <SubmissionFormContainer>
                            <Text scale="bodyS500" textAlign="left">
                                {newT(
                                    'submission.review.editMetricsInstruction',
                                    'You can edit the numbers below if the numbers provided are incorrect',
                                )}
                            </Text>
                            <NumberInputGroup>
                                <Inputs.Number
                                    label={newT('submission.table.followerCountLabel')}
                                    name="data.follower_count"
                                    inputSize="s"
                                    required
                                    width="100%"
                                />
                                {sortEngagementType(stepType, engagementTypes).map(
                                    (engagementType): JSX.Element | null => {
                                        if (engagementType === EngagementId.LIKE) {
                                            return (
                                                <Inputs.Number
                                                    label={newT('engagementMetric.like', 'Number of Likes')}
                                                    name="data.engagement_like_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.COMMENT) {
                                            return (
                                                <Inputs.Number
                                                    label={newT(
                                                        'submission.review.commentsForPartiposterHeader',
                                                        'Comments for Partiposter',
                                                    )}
                                                    name="data.engagement_comment_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.VIEW) {
                                            return (
                                                <Inputs.Number
                                                    label={
                                                        stepType ===
                                                        STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_SCREENSHOT
                                                            ? newT('engagementMetric.view', 'Number of Views')
                                                            : newT(
                                                                  'engagementMetric.videoView',
                                                                  'Number of Views (Videos)',
                                                              )
                                                    }
                                                    name="data.engagement_view_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.SHARE) {
                                            return (
                                                <Inputs.Number
                                                    label={newT('engagementMetric.share', 'Number of Shares')}
                                                    name="data.engagement_share_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.TAG) {
                                            return (
                                                <Inputs.Number
                                                    label={newT('engagementMetric.tag', 'Number of Tags')}
                                                    name="data.engagement_tag_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.REACH) {
                                            return (
                                                <Inputs.Number
                                                    label={newT('engagementMetric.reach', 'Reach')}
                                                    name="data.engagement_reach_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.IMPRESSION) {
                                            return (
                                                <Inputs.Number
                                                    label={newT('engagementMetric.impression', 'Impression')}
                                                    name="data.engagement_impression_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.STICKER_TAP) {
                                            return (
                                                <Inputs.Number
                                                    label={newT(
                                                        'engagementMetric.stickerTap',
                                                        'Number of Sticker taps',
                                                    )}
                                                    name="data.engagement_sticker_tap_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.SAVE) {
                                            return (
                                                <Inputs.Number
                                                    label={newT('engagementMetric.save')}
                                                    name="data.engagement_save_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.CTA_CLICK) {
                                            return (
                                                <Inputs.Number
                                                    label={newT(
                                                        'engagementMetric.ctaClick',
                                                        'Number of CTA Button Clicks',
                                                    )}
                                                    name="data.engagement_cta_click_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.REPLY) {
                                            return (
                                                <Inputs.Number
                                                    label={newT('engagementMetric.reply', 'Replies')}
                                                    name="data.engagement_reply_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.RETWEET) {
                                            return (
                                                <Inputs.Number
                                                    label={newT('engagementMetric.retweet', 'Retweets')}
                                                    name="data.engagement_retweet_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.ENGAGEMENT) {
                                            return (
                                                <Inputs.Number
                                                    label={newT('engagementMetric.engagement', 'Engagements')}
                                                    name="data.engagement_engagement_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.LINK_CLICK) {
                                            return (
                                                <Inputs.Number
                                                    label={newT('engagementMetric.linkClick')}
                                                    name="data.engagement_link_click_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.TOTAL_WATCH_TIME) {
                                            return (
                                                <Inputs.TextField
                                                    label={newT('engagementMetric.totalWatchTime')}
                                                    name="data.engagement_total_watch_time_second_count"
                                                    size="s"
                                                    isRequired
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.TOTAL_PLAY_TIME) {
                                            return (
                                                <Inputs.TextField
                                                    label={newT('submission.campaignReport.metric.totalPlayTime')}
                                                    name="data.engagement_total_play_time_second_count"
                                                    size="s"
                                                    isRequired
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.WATCHED_FULL_VIDEO) {
                                            return (
                                                <Inputs.Number
                                                    label={newT('engagementMetric.watchedFullVideo')}
                                                    name="data.engagement_watched_full_video_percentage_count"
                                                    inputSize="s"
                                                    required
                                                    width="100%"
                                                />
                                            )
                                        } else if (engagementType === EngagementId.AVERAGE_WATCH_TIME) {
                                            return (
                                                <Inputs.TextField
                                                    label={newT('engagementMetric.averageWatchTime')}
                                                    name="data.engagement_average_watch_time_second_count"
                                                    size="s"
                                                    isRequired
                                                    width="100%"
                                                />
                                            )
                                        }
                                        return null
                                    },
                                )}
                            </NumberInputGroup>
                            <div>
                                <Text scale="bodyS500" textAlign="left" style={{ marginBottom: 4 }}>
                                    {newT('submission.review.commentsForPartiposterHeader', 'Comments for Partiposter')}
                                </Text>
                                <Text scale="bodyS400" textAlign="left" style={{ marginBottom: 8 }}>
                                    {newT('submission.review.commentsForPartiposterSubtext')}
                                </Text>
                                <Inputs.TextArea
                                    name="comment"
                                    placeholder={newT(
                                        'placeholderText.enterCommentForPartipostertForPartiposter',
                                        'Write your comment here. E.g. Thanks for your submission.',
                                    )}
                                    minTextAreaHeight={64}
                                    maxTextAreaHeight={332}
                                />
                            </div>
                            <div className="btn-group">
                                <RectangleButton
                                    form="outlined"
                                    size="l"
                                    label={newT('button.cancel')}
                                    onClick={handleClear}
                                />
                                <RectangleButton form="filled" size="l" type="submit" label={newT('button.approve')} />
                            </div>
                        </SubmissionFormContainer>
                    </Form>
                )
            }}
        </Formik>
    )
}
